import React, { useState } from 'react';

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

import { Code } from '~/components/Common/Code';

import { Payment } from '~/types/services/payment';

import { PaymentPlanAdditionalInfo } from './PaymentPlanAdditionalInfo';

interface Props {
  payment: Payment;
  paymentPlanComponent?: App.PaymentPlanComponent;
}

type StripeCardDetails = {
  three_d_secure?: {
    succeeded?: boolean;
    authentication_flow?: string;
  };
  last4?: string;
  last_4?: string;
  brand?: string;
  network?: string;
};

type StripeCharge = {
  outcome?: {
    risk_score?: number;
  };
  payment_method_details?: {
    card?: StripeCardDetails;
  };
};

type StripeResponseJson = {
  data?: {
    object?: {
      id?: string;
      charges?: {
        data?: Array<StripeCharge>;
      };
      last_payment_error?: {
        code?: string;
      };
    };
  };
};

function getStripeResponseJson(payment: Payment): StripeResponseJson {
  return payment.response_json as StripeResponseJson;
}

const format3DSecure = (threeDSecure: StripeCardDetails['three_d_secure'] | undefined | null, payment: Payment) => {
  if (payment.intent === 'refund') {
    return '';
  }

  if (threeDSecure === null) {
    return 'not requested 🟡';
  }
  if (threeDSecure?.succeeded) {
    return `succeeded via ${threeDSecure.authentication_flow || 'frictionless'} 🟢`;
  } else if (threeDSecure?.succeeded == false) {
    return `failed via ${threeDSecure.authentication_flow || 'unknown'} 🔴`;
  }
  if (threeDSecure === undefined) {
    const error = getStripeResponseJson(payment).data?.object?.last_payment_error?.code;
    if (error) {
      return '(no info). Error: ' + error + ' 🔴';
    }
  }

  return 'unknown - ToDo, handle this';
};

function StripePaymentLine({ payment, paymentPlanComponent }: Props) {
  const [openDebug, setOpenDebug] = useState(false);
  const [debugJson, setDebugJson] = useState<string>('');

  const stripeResponseJson = getStripeResponseJson(payment);
  const charge = stripeResponseJson.data?.object?.charges?.data?.[0];
  const riskScore = charge?.outcome?.risk_score;
  const last4 = charge?.payment_method_details?.card?.last4 || charge?.payment_method_details?.card?.last_4;
  const cardBrand = charge?.payment_method_details?.card?.brand;
  const cardNetwork = charge?.payment_method_details?.card?.network;
  const pspName = payment.psp_name;
  const threeDSecure =
    charge?.payment_method_details?.card &&
    Object.prototype.hasOwnProperty.call(charge.payment_method_details.card, 'three_d_secure')
      ? charge.payment_method_details.card.three_d_secure
      : undefined;

  return (
    <>
      <Box>
        {Math.abs(Number(payment.amount)).toLocaleString('en-US', { minimumFractionDigits: 0 })}{' '}
        <a
          href={`https://dashboard.stripe.com/payments/${stripeResponseJson.data?.object?.id ?? ''}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {payment.type}
        </a>{' '}
        <PaymentPlanAdditionalInfo paymentPlanComponent={paymentPlanComponent} /> {last4 && <span> *{last4}</span>}
        {cardBrand && <span> {cardBrand}</span>}
        {cardNetwork && cardNetwork !== cardBrand && <span> ({cardNetwork})</span>}
        {pspName && pspName !== 'card' && <span> via {pspName}</span>}
        {riskScore >= 0 && <span> risk {riskScore}</span>}{' '}
        <Button
          variant="text"
          color="inherit"
          sx={{
            textDecoration: 'underline',
            background: 'none',
            border: 'none',
            font: 'inherit',
            p: 0,
            minWidth: 0,
            textTransform: 'none',
          }}
          onClick={(e) => {
            e.preventDefault();
            setOpenDebug(true);
            setDebugJson(JSON.stringify(payment.response_json, null, 2));
          }}
        >
          json
        </Button>
        <br />
        {format3DSecure(threeDSecure, payment) && (
          <span>
            3DS: {format3DSecure(threeDSecure, payment)}
            <br />
          </span>
        )}
      </Box>

      <Dialog open={openDebug} onClose={() => setOpenDebug(false)} maxWidth="md">
        <DialogTitle>Payment Response Details</DialogTitle>
        <DialogContent>
          <Code value={debugJson} lang="json" />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setOpenDebug(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export { StripePaymentLine };
